import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useI18n } from '@/hooks/useI18n';
import estilos from './acceso.module.css';

/**
 * La puerta de la demostración: un inicio de sesión que siempre abre.
 *
 * Existe para poder enseñar el producto desde la silla del cliente y no desde
 * una URL suelta: lo primero que hace una marca es entrar a su panel, y la
 * demo empieza donde empieza la marca. **No autentica nada** — no hay cuenta,
 * no hay sesión, no se llama a Cognito ni a ninguna API, y el botón lleva
 * siempre al mismo sitio.
 *
 * Tres decisiones que conviene no deshacer sin pensarlo:
 *
 * - **Los campos son de solo lectura y traen puestas unas credenciales
 *   inventadas.** Un formulario público con un campo de contraseña que acepta
 *   cualquier cosa tiene exactamente la forma de una pantalla de phishing, y
 *   alguien acabaría tecleando ahí su contraseña real. Así no puede.
 * - **Lo dice en pantalla, arriba y antes del formulario.** La regla del
 *   diccionario de la demo es que nada afirme que hay una medición real; una
 *   pantalla de acceso que no se anuncia como simulación la rompería sin
 *   escribir una sola cifra falsa.
 * - **No reusa `AuthCard`**, que avisaba cuando faltaba el user pool. Aquí no
 *   falta nada: no se usa.
 *
 * Los estilos (`acceso.module.css`) eran los de las pantallas de sesión de
 * verdad, y se heredaron cuando aquéllas se fueron con el backend el
 * 2026-09-18. Que este login de mentira tenga la pinta de un login de verdad
 * es justo lo que hace la demostración: lo que se enseña es el paso que da la
 * marca, y un login con otra pinta no lo enseña.
 */

/** Credenciales de mentira, visibles a propósito. `.example` no existe (RFC 2606). */
const CORREO_DEMO = 'marca@acme.example';
const CONTRASENA_DEMO = 'demostracion';

export function AccesoDemoPage() {
  const { t } = useI18n();
  const a = t.demo.acceso;
  const navigate = useNavigate();

  useDocumentMeta(t.meta.accesoDemo);

  function entrar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    navigate('/demo/panel');
  }

  return (
    <div className={estilos.page}>
      <div className={estilos.card}>
        <h1 className={estilos.title}>{a.titulo}</h1>
        <p className={estilos.subtitle}>{a.subtitulo}</p>

        <p className={[estilos.alert, estilos.alertWarn].join(' ')} role="status">
          {a.aviso}
        </p>

        <form onSubmit={entrar} noValidate>
          <div className={estilos.field}>
            <label htmlFor="demo-correo">{a.correo}</label>
            <input id="demo-correo" type="text" value={CORREO_DEMO} readOnly tabIndex={-1} />
          </div>

          <div className={estilos.field}>
            <label htmlFor="demo-contrasena">{a.contrasena}</label>
            <input
              id="demo-contrasena"
              type="password"
              value={CONTRASENA_DEMO}
              readOnly
              tabIndex={-1}
            />
            <p className={estilos.hint}>{a.credenciales}</p>
          </div>

          <div className={estilos.submitRow}>
            <Button type="submit" block>
              <PlayCircle size={18} aria-hidden="true" /> {a.entrar}
            </Button>
          </div>

          {/* Una sola salida: "Entrar con mi cuenta" llevaba al login, que se
              fue con el backend. */}
          <div className={estilos.footerLinks}>
            <Link to="/">{a.volverAlSitio}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
